/**
 * Expand a feature's range-compressed `properties.surfaces` into a flat
 * per-point array.
 *
 * The server ships surfaces as `[[startIndex, endIndex, surfaceInt], …]`
 * relative to that feature's own flattened point sequence (see
 * `hike_feature_collection.rb`). The elevation chart needs one entry per
 * coordinate, index-for-index with the flattened coordinate array — a
 * mismatch there silently mis-colours the whole profile, since the two are
 * zipped positionally.
 *
 * Points no range covers stay `undefined` rather than defaulting to a
 * surface: "not surveyed" and "surveyed as unknown" are different, and the
 * chart's graceful-degradation heuristic counts them separately.
 */
export function expandSurfaceRanges(
  ranges: Array<[number, number, number]> | null | undefined,
  totalPoints: number
): (number | undefined)[] {
  if (!Number.isFinite(totalPoints) || totalPoints <= 0) return [];

  const surfaces: (number | undefined)[] = new Array(totalPoints).fill(undefined);
  if (!Array.isArray(ranges)) return surfaces;

  ranges.forEach((range) => {
    if (!Array.isArray(range) || range.length < 3) return;
    const [start, end, surfaceInt] = range;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;

    // Ranges are inclusive at both ends, and clamped: a range running past the
    // feature's own points would otherwise write off the end of the array.
    for (let i = Math.max(0, start); i <= end && i < totalPoints; i++) {
      surfaces[i] = surfaceInt;
    }
  });

  return surfaces;
}

/** Total coordinate count across a MultiLineString's parts. */
export function multiLineStringPointCount(coordinates: number[][][] | null | undefined): number {
  if (!Array.isArray(coordinates)) return 0;
  return coordinates.reduce(
    (sum, lineString) => sum + (Array.isArray(lineString) ? lineString.length : 0),
    0
  );
}
